import {
  Button,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import DeleteIcon from '@material-ui/icons/Delete'
import PropTypes from 'prop-types'
import React from 'react'

import { Loading } from 'app/components/generic/LottieComponents'
import constants from 'app/constants'
import { confirm, eventAlert, eventEmitter } from 'app/helpers'

import { FilterHeadCell } from './shared/AtHomeCrudPage'
import AtHomeFormModal from './shared/AtHomeFormModal'

const useStyles = makeStyles((theme) => ({
  card: { height: '100%' },
  head: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1)
  },
  hint: { color: theme.palette.text.secondary, marginBottom: theme.spacing(1) },
  // The card is half the page; let a wide table scroll inside it rather
  // than clip its last columns (the Categories column made this bite).
  scroll: { overflowX: 'auto' },
  wrap: { whiteSpace: 'normal', maxWidth: 260 },
  row: { '&:hover': { background: theme.palette.action.hover } },
  muted: { color: theme.palette.text.disabled },
  empty: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(3, 0),
    textAlign: 'center'
  }
}))

// What a cell reads as, for its header dropdown; null when the column renders
// a node (the category icon) rather than text, which cannot be filtered on.
const cellText = (c, r) => {
  const v = c.render ? c.render(r) : r[c.name]
  return typeof v === 'string' || typeof v === 'number' ? String(v) : null
}

/**
 * A compact, dense reference-data list: title, one-line hint, "+ Add" button,
 * rows with an inline Edit. For short lists (apartments, categories) where the
 * full datatable's toolbar, filters and pagination footer are noise. Each
 * column carries the same header dropdown as the At Home grids, filtering
 * these rows here (owner, 2026-10-19).
 */
const SetupList = ({
  title,
  hint,
  entityLabel,
  columns,
  fields,
  fetchList,
  createItem,
  updateItem,
  deleteItem,
  emptyValues,
  formToPayload,
  // Optional: shape a row into form values (default: the row itself)
  rowToForm,
  // Width of the add/edit dialog ('sm' default; 'md' for the module editor)
  formMaxWidth,
  // Optional: (values) => node, shown beside the form and redrawn as it is
  // typed. Categories pass the Home card a parent will see.
  formPreview,
  // Optional extra control per row, rendered before Edit: (row, reload) => node
  rowAction
}) => {
  const classes = useStyles()
  const [rows, setRows] = React.useState(null)
  const [modal, setModal] = React.useState({ open: false, row: null })
  // column name -> selected value ('' = all)
  const [filters, setFilters] = React.useState({})

  // The dropdown values per column, A→Z. A column whose cells are not text is
  // left without a dropdown.
  const distinct = React.useMemo(() => {
    const out = {}
    columns.forEach((c) => {
      const seen = []
      const text = (rows || []).every((r) => {
        const v = cellText(c, r)
        if (v === null) return false
        if (!seen.includes(v)) seen.push(v)
        return true
      })
      if (text) out[c.name] = [...seen].sort((a, b) => a.localeCompare(b))
    })
    return out
  }, [rows, columns])

  const shown = React.useMemo(() => {
    const picked = Object.entries(filters).filter(([, v]) => v)
    if (!picked.length) return rows || []
    return (rows || []).filter((r) =>
      picked.every(([name, v]) => {
        const c = columns.find((x) => x.name === name)
        return c ? cellText(c, r) === v : true
      })
    )
  }, [rows, columns, filters])

  const load = React.useCallback(() => {
    fetchList()
      .then(setRows)
      .catch(() => setRows([]))
  }, [fetchList])
  React.useEffect(load, [load])

  const onDelete = (r) =>
    confirm(
      async () => {
        try {
          await deleteItem(r.uuid)
          eventAlert(`${entityLabel} deleted`, constants.SUCCESS)
          load()
        } catch (error) {
          // request.js already surfaces a toast
        }
      },
      `This ${entityLabel.toLowerCase()} will be deleted. This cannot be undone.`,
      undefined,
      { title: 'Are you sure?', confirmButtonText: 'Delete' }
    )

  const submit = async (values) => {
    try {
      const payload = formToPayload ? formToPayload(values) : values
      if (modal.row) await updateItem(modal.row.uuid, payload)
      else await createItem(payload)
      eventAlert(
        `${entityLabel} ${modal.row ? 'updated' : 'added'}`,
        constants.SUCCESS
      )
      setModal({ open: false, row: null })
      load()
    } catch (error) {
      // request.js already surfaces a toast; re-enable Save so the user can fix and retry
      eventEmitter.emit(constants.MODAL_FORM_SAVE_ERROR)
    }
  }

  return (
    <Card variant="outlined" className={classes.card}>
      <CardContent>
        <div className={classes.head}>
          {title ? <Typography variant="h6">{title}</Typography> : <span />}
          <Button
            size="small"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setModal({ open: true, row: null })}
          >
            Add
          </Button>
        </div>
        {hint && (
          <Typography variant="body2" className={classes.hint}>
            {hint}
          </Typography>
        )}
        {!rows ? (
          <Loading />
        ) : rows.length === 0 ? (
          <div className={classes.empty}>
            Nothing here yet. Use “Add” to create the first one.
          </div>
        ) : (
          <div className={classes.scroll}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {columns.map((c) => (
                    <FilterHeadCell
                      key={c.name}
                      meta={{ name: c.name, label: c.label, sort: false }}
                      align={c.align}
                      value={filters[c.name] || ''}
                      values={distinct[c.name]}
                      onChange={(v) =>
                        setFilters((f) => ({ ...f, [c.name]: v }))
                      }
                    />
                  ))}
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {shown.map((r) => (
                  <TableRow
                    key={r.uuid}
                    className={`${classes.row} ${
                      r.active === false ? classes.muted : ''
                    }`}
                  >
                    {columns.map((c) => (
                      <TableCell
                        key={c.name}
                        align={c.align}
                        className={`${
                          r.active === false ? classes.muted : ''
                        } ${c.wrap ? classes.wrap : ''}`}
                      >
                        {c.render ? c.render(r) : r[c.name]}
                      </TableCell>
                    ))}
                    <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>
                      {rowAction && rowAction(r, load)}
                      <Button
                        size="small"
                        onClick={() => setModal({ open: true, row: r })}
                      >
                        Edit
                      </Button>
                      {deleteItem && (
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => onDelete(r)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
      <AtHomeFormModal
        maxWidth={formMaxWidth}
        preview={formPreview}
        open={modal.open}
        title={`${modal.row ? 'Edit' : 'New'} ${entityLabel}`}
        fields={fields}
        initialValues={
          modal.row
            ? rowToForm
              ? rowToForm(modal.row)
              : modal.row
            : emptyValues
        }
        onSubmit={submit}
        onClose={() => setModal({ open: false, row: null })}
      />
    </Card>
  )
}

SetupList.propTypes = {
  title: PropTypes.string,
  hint: PropTypes.string,
  entityLabel: PropTypes.string.isRequired,
  columns: PropTypes.array.isRequired,
  fields: PropTypes.array.isRequired,
  fetchList: PropTypes.func.isRequired,
  createItem: PropTypes.func.isRequired,
  updateItem: PropTypes.func.isRequired,
  deleteItem: PropTypes.func,
  emptyValues: PropTypes.object,
  formToPayload: PropTypes.func,
  rowToForm: PropTypes.func,
  formMaxWidth: PropTypes.string,
  formPreview: PropTypes.func,
  rowAction: PropTypes.func
}

export default SetupList
